import { useMemo, useState } from 'react';
import type { PlayTreeNode } from './DiffereStatsPlayTreeDrill';
import { DiffereStatsCardSequence } from './DiffereStatsCardChip';
import { formatPlayTreePathLabelsFromRoot, resolvePlayTreePathNodes } from './differeStatsTreeLabel';
import PlayTreeNodeDeciles, { globalDecileScaleMax } from './PlayTreeNodeDeciles';
import {
  flattenPlayTreePaths,
  maxSequenceCountAmongPaths,
  shouldShowPlayTreeHypothesis,
  sortPlayTreePaths,
  type PlayTreePathSort,
} from './playTreePaths';

const MIN_COUNT_DEFAULT = 5;

export interface PlayTreePathLabelInfo {
  humanCardsByTrick: number[][];
  humanLedByTrick?: Array<boolean | undefined>;
}

function findNodeAtPath(root: PlayTreeNode, path: string[]): PlayTreeNode | null {
  let nodes = root.children;
  let found: PlayTreeNode | null = null;
  for (const key of path) {
    found = nodes.find(n => n.key === key) ?? null;
    if (!found) return null;
    nodes = found.children;
  }
  return found;
}

function pathEquals(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((k, i) => k === b[i]);
}

export default function PlayTreePathList({
  root,
  maxDepth,
  selectedPath,
  onSelectPath,
  pathLabelByKey,
  consensusPathKeys = [],
  consensusDepth = 0,
  consensusBranchSize = 0,
  consensusStopReason,
}: {
  root: PlayTreeNode;
  maxDepth: number;
  selectedPath: string[];
  onSelectPath: (path: string[]) => void;
  /** Libellés riches par chemin (cartes + entame). */
  pathLabelByKey?: Map<string, PlayTreePathLabelInfo | string>;
  consensusPathKeys?: string[];
  consensusDepth?: number;
  consensusBranchSize?: number;
  consensusStopReason?: string;
}) {
  const [sort, setSort] = useState<PlayTreePathSort>('total');
  const [hideLowCount, setHideLowCount] = useState(true);
  const pathKey = selectedPath.join('|');
  const consensusKey = consensusPathKeys.join('|');

  /** Ligne dédiée seulement si le préfixe consensus n'est pas déjà une hypothèse complète. */
  const showConsensusExtraRow = consensusDepth > 0
    && consensusPathKeys.length === consensusDepth
    && consensusDepth !== maxDepth;

  const { rows, totalCount, hiddenCount, decileScaleMax, consensusNode } = useMemo(() => {
    const flat = flattenPlayTreePaths(root, maxDepth);
    const sorted = sortPlayTreePaths(flat, sort);
    const maxSeqCount = maxSequenceCountAmongPaths(flat);
    const visible = sorted.filter(row =>
      shouldShowPlayTreeHypothesis(row.node, MIN_COUNT_DEFAULT, hideLowCount, maxSeqCount),
    );
    const cNode = consensusPathKeys.length > 0 ? findNodeAtPath(root, consensusPathKeys) : null;
    return {
      rows: visible,
      totalCount: sorted.length,
      hiddenCount: sorted.length - visible.length,
      decileScaleMax: globalDecileScaleMax([
        ...visible.map(r => r.node),
        ...(cNode ? [cNode] : []),
      ]),
      consensusNode: cNode,
    };
  }, [root, maxDepth, sort, hideLowCount, consensusKey]);

  if (totalCount === 0 && !showConsensusExtraRow) {
    return <p className="admin-muted">Aucune séquence complète sur {maxDepth} pli(s).</p>;
  }

  const renderRow = (
    path: string[],
    node: PlayTreeNode,
    opts?: { consensusExtra?: boolean; consensusMatch?: boolean },
  ) => {
    const key = path.join('|');
    const isSelected = pathKey === key;
    const isConsensus = Boolean(opts?.consensusExtra || opts?.consensusMatch);
    const labelInfo = pathLabelByKey?.get(key);
    const fromNodes = resolvePlayTreePathNodes(root, path);
    const cardsByTrick = typeof labelInfo === 'object' && labelInfo
      ? labelInfo.humanCardsByTrick
      : fromNodes.map(n => n.humanCards ?? []);
    const ledByTrick = typeof labelInfo === 'object' && labelInfo
      ? labelInfo.humanLedByTrick
      : fromNodes.map(n => n.humanLed);
    return (
      <li key={opts?.consensusExtra ? `consensus:${key}` : key}>
        <button
          type="button"
          className={[
            'differe-stats-play-tree-list-row',
            isSelected ? 'active' : '',
            isConsensus ? 'differe-stats-play-tree-list-row--consensus' : '',
          ].filter(Boolean).join(' ')}
          onClick={() => onSelectPath(path)}
          title={
            isConsensus
              ? `Préfixe consensus adaptatif (pli ${consensusDepth}) — ${opts?.consensusExtra && consensusBranchSize > 0 ? consensusBranchSize : node.count} joueur(s)${consensusStopReason ? ` ; arrêt: ${consensusStopReason}` : ''}. Cliquer pour lister.`
              : `${node.count} joueur(s) — cliquer pour lister`
          }
        >
          <span className="differe-stats-play-tree-list-sequence">
            {typeof labelInfo === 'string'
              ? labelInfo
              : (cardsByTrick.length > 0
                ? <DiffereStatsCardSequence cardsByTrick={cardsByTrick} ledByTrick={ledByTrick} />
                : formatPlayTreePathLabelsFromRoot(root, path))}
          </span>
          {isConsensus && (
            <span className="differe-stats-consensus-badge" title="Préfixe consensus (Prof. adapt.)">
              Consensus pli {consensusDepth}
            </span>
          )}
          <span className="differe-stats-play-tree-list-count">
            {opts?.consensusExtra && consensusBranchSize > 0 ? consensusBranchSize : node.count}
          </span>
          <PlayTreeNodeDeciles node={node} variant="inline" scaleMax={decileScaleMax} />
        </button>
      </li>
    );
  };

  return (
    <div className="differe-stats-play-tree-list">
      <div className="differe-stats-play-tree-list-toolbar">
        <span className="admin-muted">
          {rows.length} / {totalCount} hypothèse{totalCount > 1 ? 's' : ''}
          {hiddenCount > 0 ? ` (${hiddenCount} masquée${hiddenCount > 1 ? 's' : ''})` : ''}
          {' '}— tri :
        </span>
        <button
          type="button"
          className={`admin-btn admin-btn-sm${sort === 'total' ? ' admin-btn-primary' : ''}`}
          onClick={() => setSort('total')}
        >
          Effectif total
        </button>
        <button
          type="button"
          className={`admin-btn admin-btn-sm${sort === 'topDecile' ? ' admin-btn-primary' : ''}`}
          onClick={() => setSort('topDecile')}
          title="Tri par nombre de joueurs dans le décile D1 (top 10 % à la donne)"
        >
          Effectif top 10 % (D1)
        </button>
        <label className="differe-stats-check differe-stats-play-tree-filter">
          <input
            type="checkbox"
            checked={hideLowCount}
            onChange={e => setHideLowCount(e.target.checked)}
          />
          Masquer effectif &lt; {MIN_COUNT_DEFAULT}
          <span className="admin-muted"> (sauf seq. max / 1er / 2e / 3e / dernier)</span>
        </label>
        <span className="admin-muted differe-stats-lead-legend">
          <span className="differe-stats-chip differe-stats-chip--lead differe-stats-chip--legend">Ent.</span>
          = entame
        </span>
      </div>
      {rows.length === 0 && !showConsensusExtraRow ? (
        <p className="admin-muted">Aucune hypothèse visible avec les filtres actuels.</p>
      ) : (
        <ul className="differe-stats-play-tree-list-rows">
          {showConsensusExtraRow && consensusNode && renderRow(consensusPathKeys, consensusNode, { consensusExtra: true })}
          {rows.map(({ path, node }) => {
            // Même profondeur que le consensus → surligner la ligne existante (pas de doublon).
            const consensusMatch = consensusDepth > 0
              && consensusDepth === maxDepth
              && pathEquals(path, consensusPathKeys);
            return renderRow(path, node, { consensusMatch });
          })}
        </ul>
      )}
    </div>
  );
}
